import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MapPin, Calendar, Users, User, Edit3, LogOut, AlertTriangle, UserPlus, FileText } from "lucide-react";
import { POSITIONS, getPositionLabel } from "@/utils/positions";

const MatchDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState("central-midfielder");
  const [myParticipation, setMyParticipation] = useState(null);
  const [changePositionOpen, setChangePositionOpen] = useState(false);
  const [newPosition, setNewPosition] = useState("");
  const [changing, setChanging] = useState(false);

  useEffect(() => {
    fetchMatch();
    fetchMyParticipation();
  }, [id]);

  const fetchMatch = async () => {
    try {
      const response = await axios.get(`${API}/matches/${id}`);
      setMatch(response.data);
    } catch (error) {
      console.error("Error fetching match:", error);
      toast.error("Maç bilgileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchMyParticipation = async () => {
    try {
      const response = await axios.get(`${API}/matches/${id}/my-participation`);
      setMyParticipation(response.data);
      if (response.data.currentPosition) {
        setNewPosition(response.data.currentPosition);
      }
    } catch (error) {
      console.error("Error fetching participation:", error);
    }
  };

  const handleJoinMatch = async () => {
    setJoining(true);
    try {
      const response = await axios.post(`${API}/matches/${id}/join`, {
        position: selectedPosition.split('-')[0],
        detailedPosition: selectedPosition
      });
      setMatch(response.data);
      fetchMyParticipation();
      toast.success("Maça katıldın!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Maça katılınamadı");
    } finally {
      setJoining(false);
    }
  };

  const handleChangePosition = async () => {
    if (!newPosition || newPosition === myParticipation?.currentPosition) {
      toast.error("Yeni bir pozisyon seçin");
      return;
    }

    setChanging(true);
    try {
      const response = await axios.put(`${API}/matches/${id}/change-position`, {
        newPosition: newPosition.split('-')[0],
        newDetailedPosition: newPosition
      });
      
      toast.success(response.data.message || "Pozisyon değiştirildi");
      
      setChangePositionOpen(false);
      fetchMatch();
      fetchMyParticipation();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Pozisyon değiştirilemedi");
    } finally {
      setChanging(false);
    }
  };

  const handleLeaveMatch = async () => {
    if (!confirm("Maçtan ayrılmak istediğinize emin misiniz?")) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/matches/${id}/leave`);
      
      toast.success(response.data.message);
      if (response.data.penalty < 0) {
        toast.warning(response.data.penaltyMessage, { duration: 5000 });
      }
      
      navigate("/");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Maçtan ayrılamadınız");
    }
  };

  const getPositionColor = (position) => {
    const colors = {
      goalkeeper: "bg-yellow-500",
      defender: "bg-blue-500",
      midfielder: "bg-green-500",
      forward: "bg-red-500",
      substitute: "bg-gray-500"
    };
    return colors[position] || "bg-gray-500";
  };

  const getPositionCoordinates = (detailedPosition) => {
    // Profesyonel futbol formasyonuna göre koordinatlar (x: 0-100, y: 0-100)
    // x: 0 (sol) - 100 (sağ), y: 0 (hücum) - 100 (savunma)
    const positions = {
      // Kaleciler
      "goalkeeper": { x: 50, y: 95 },
      "sweeper-keeper": { x: 50, y: 90 },
      
      // Defans hattı
      "left-back": { x: 20, y: 78 },
      "left-center-back": { x: 38, y: 80 },
      "right-center-back": { x: 62, y: 80 },
      "right-back": { x: 80, y: 78 },
      "sweeper": { x: 50, y: 85 },
      "left-wing-back": { x: 15, y: 70 },
      "right-wing-back": { x: 85, y: 70 },
      
      // Orta saha
      "left-central-midfielder": { x: 35, y: 58 },
      "central-midfielder": { x: 50, y: 60 },
      "right-central-midfielder": { x: 65, y: 58 },
      "deep-lying-playmaker": { x: 50, y: 68 },
      "attacking-midfielder": { x: 50, y: 48 },
      
      // Kanatlar
      "left-winger": { x: 18, y: 50 },
      "right-winger": { x: 82, y: 50 },
      
      // Forvetler
      "left-forward": { x: 32, y: 32 },
      "right-forward": { x: 68, y: 32 },
      "second-striker": { x: 50, y: 35 },
      "center-forward": { x: 50, y: 25 },
      "false-nine": { x: 50, y: 40 },
      
      // Yedek
      "substitute": { x: 8, y: 3 }
    };
    
    return positions[detailedPosition] || { x: 50, y: 50 };
  };

  const renderFieldPosition = (player, index) => {
    // Detaylı pozisyon varsa onu kullan, yoksa eski sistemi kullan
    let x, y;
    
    if (player.detailedPosition) {
      const coords = getPositionCoordinates(player.detailedPosition);
      x = coords.x;
      y = coords.y;
    } else if (player.x && player.y) {
      // Manuel yerleştirilmişse
      x = player.x * 100;
      y = player.y * 100;
    } else {
      // Fallback: eski sistem
      const defaultPositions = {
        goalkeeper: { x: 50, y: 90 },
        defender: { x: 20 + (index % 4) * 20, y: 75 },
        midfielder: { x: 25 + (index % 3) * 25, y: 50 },
        forward: { x: 30 + (index % 3) * 20, y: 25 },
        substitute: { x: 10 + (index % 5) * 20, y: 5 }
      };

      const pos = defaultPositions[player.position] || { x: 50, y: 50 };
      x = pos.x;
      y = pos.y;
    }

    return (
      <div
        key={player.userId}
        className="field-player absolute transform -translate-x-1/2 -translate-y-1/2"
        style={{ left: `${x}%`, top: `${y}%` }}
        data-testid={`field-player-${player.userId}`}
      >
        <div className="flex flex-col items-center">
          <Avatar className={`h-12 w-12 border-4 ${getPositionColor(player.position)} border-white shadow-lg`}>
            <AvatarImage src={player.profileImage} />
            <AvatarFallback className="bg-white text-black font-bold">
              {player.name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div className="mt-1 bg-white px-2 py-0.5 rounded-full text-xs font-semibold shadow-md whitespace-nowrap">
            {player.name}
          </div>
          <Badge className={`mt-1 ${getPositionColor(player.position)} text-white text-xs`}>
            {getPositionLabel(player.detailedPosition || player.position)}
          </Badge>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <Card className="p-12 text-center">
            <p className="text-gray-500 text-lg">Maç bulunamadı</p>
          </Card>
        </div>
      </div>
    );
  }

  const isPlayerInMatch = match.players.some(p => p.userId === user.id);
  const isCaptain = match.captainId === user.id;

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300" data-testid="match-detail">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Match Header */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-emerald-600 to-green-700 p-6 text-white">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">{match.venueName}</h1>
            <div className="flex flex-wrap gap-4 text-emerald-50">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                <span>{match.date} - {match.time}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <span>Kaptan: {match.captainName}</span>
              </div>
              <Badge className="bg-white text-emerald-600">
                {match.players.length}/{match.maxPlayers} Oyuncu
              </Badge>
            </div>
          </div>
        </Card>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Football Field */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Saha Yerleşimi</CardTitle>
              <CardDescription>Oyuncuların pozisyonlara göre yerleşimi</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative w-full aspect-[3/4] bg-gradient-to-b from-emerald-600 to-green-700 rounded-lg overflow-hidden shadow-2xl" data-testid="football-field">
                {/* Field Lines */}
                <div className="absolute inset-0">
                  {/* Center Line */}
                  <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-white opacity-50"></div>
                  {/* Center Circle */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 border-2 border-white rounded-full opacity-50"></div>
                  {/* Goal Areas */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-16 border-2 border-white border-t-0 opacity-50"></div>
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-32 h-16 border-2 border-white border-b-0 opacity-50"></div>
                  {/* Penalty Areas */}
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-48 h-24 border-2 border-white border-t-0 opacity-30"></div>
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-48 h-24 border-2 border-white border-b-0 opacity-30"></div>
                </div>

                {/* Players */}
                {match.players.map((player, index) => renderFieldPosition(player, index))}

                {match.players.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-white text-lg font-semibold">Henüz oyuncu yok</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Players List & Join */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-emerald-600" />
                  Oyuncular
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {match.players.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">Henüz oyuncu yok</p>
                  ) : (
                    match.players.map((player) => (
                      <div 
                        key={player.userId} 
                        className="flex items-center gap-3 p-3 border rounded-lg hover:border-emerald-500 transition-colors cursor-pointer"
                        onClick={() => navigate(`/profile/${player.userId}`)}
                        data-testid={`player-item-${player.userId}`}
                      >
                        <Avatar>
                          <AvatarImage src={player.profileImage} />
                          <AvatarFallback>{player.name.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <p className="font-semibold">{player.name}</p>
                          <p className="text-sm text-gray-600">
                            {getPositionLabel(player.detailedPosition || player.position)}
                          </p>
                        </div>
                        <Badge className={getPositionColor(player.position)}>
                          {getPositionLabel(player.detailedPosition || player.position)}
                        </Badge>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {!isPlayerInMatch && match.players.length < match.maxPlayers && (
              <Card>
                <CardHeader>
                  <CardTitle>Maça Katıl</CardTitle>
                  <CardDescription>Pozisyonunu seç ve katıl</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-700 flex items-center gap-2">
                      <span className="text-lg">💰</span>
                      Maça katılmak için <strong>15 kredi</strong> gereklidir
                    </p>
                  </div>
                  <Select value={selectedPosition} onValueChange={setSelectedPosition}>
                    <SelectTrigger data-testid="join-position-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[400px]">
                      {POSITIONS.filter(p => p.value !== 'substitute').map((pos) => (
                        <SelectItem key={pos.value} value={pos.value}>
                          {pos.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    onClick={handleJoinMatch}
                    disabled={joining}
                    className="w-full bg-gradient-to-r from-emerald-500 to-green-600 dark:from-dark-card dark:to-dark-accent"
                    data-testid="join-match-btn"
                  >
                    {joining ? "Katılıyor..." : "Maça Katıl (15💰)"}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Captain Management - Always show for captain */}
            {match?.captainId === user?.id && (
              <Card className="border-purple-200 bg-purple-50 mb-4">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <UserPlus className="h-5 w-5 text-purple-600" />
                    Kaptan Yönetimi
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button
                    className="w-full bg-gradient-to-r from-purple-500 to-purple-600"
                    onClick={() => navigate(`/player-pool?matchId=${match.id}`)}
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Oyuncu Seç ve Ekle
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full border-purple-300 hover:bg-purple-100"
                    onClick={() => navigate("/captain/applications")}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Başvuruları Yönet
                  </Button>
                  <div className="p-3 bg-purple-100 border border-purple-200 rounded-lg">
                    <p className="text-xs text-purple-800">
                      <strong>Kaptan:</strong> Oyuncu Seç butonuyla oyuncu havuzundan direkt oyuncu ekleyebilir, başvuruları onaylayabilir ve takımı yönetebilirsiniz.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            {isPlayerInMatch && myParticipation && (
              <div className="space-y-4">
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-emerald-700 mb-4">
                      <User className="h-5 w-5" />
                      <p className="font-semibold">Bu maçtasın!</p>
                    </div>
                    <p className="text-sm text-emerald-600">
                      Pozisyon: <span className="font-bold">{getPositionLabel(myParticipation.currentPosition)}</span>
                    </p>
                  </CardContent>
                </Card>

                {/* Player Management - Only for non-captains */}
                {match?.captainId !== user?.id && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Katılımı Yönet</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => setChangePositionOpen(true)}
                        disabled={!myParticipation.canChangePosition}
                      >
                        <Edit3 className="h-4 w-4 mr-2" />
                        Pozisyon Değiştir
                      </Button>
                      
                      {!myParticipation.canChangePosition && (
                        <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs text-blue-700">
                            ⏰ Son 12 saat içinde pozisyon değişikliği sadece kaptan yapabilir
                          </p>
                        </div>
                      )}
                      
                      <Button
                        variant="destructive"
                        className="w-full"
                        onClick={handleLeaveMatch}
                      >
                        <LogOut className="h-4 w-4 mr-2" />
                        Maçtan Ayrıl
                      </Button>
                      
                      {myParticipation.leavePenalty && myParticipation.leavePenalty.penalty < 0 && (
                        <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm">
                              <p className="font-semibold text-orange-800 mb-1">Uyarı - Maçtan Ayrılma Cezası</p>
                              <p className="text-orange-700">{myParticipation.leavePenalty.message}</p>
                              <p className="text-orange-700 mt-1 font-semibold">
                                Maçtan ayrılırsanız {myParticipation.leavePenalty.penalty} puan kaybedersiniz.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>

    {/* Change Position Dialog */}
    <Dialog open={changePositionOpen} onOpenChange={setChangePositionOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Pozisyon Değiştir</DialogTitle>
          <DialogDescription>
            Yeni pozisyonunu seç
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Mevcut Pozisyon</Label>
            <div className="p-3 bg-gray-100 rounded-lg">
              <p className="font-semibold">{getPositionLabel(myParticipation?.currentPosition)}</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Yeni Pozisyon</Label>
            <Select value={newPosition} onValueChange={setNewPosition}>
              <SelectTrigger>
                <SelectValue placeholder="Pozisyon seç" />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {POSITIONS.filter(p => p.value !== 'substitute').map((pos) => (
                  <SelectItem key={pos.value} value={pos.value}>
                    {pos.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-700">
              ✅ Pozisyon değişikliği ücretsizdir ve puan kaybına neden olmaz.
            </p>
          </div>

          <Button
            onClick={handleChangePosition}
            disabled={changing || !newPosition || newPosition === myParticipation?.currentPosition}
            className="w-full bg-gradient-to-r from-emerald-500 to-green-600 dark:from-dark-card dark:to-dark-accent"
          >
            {changing ? "Değiştiriliyor..." : "Pozisyonu Değiştir"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default MatchDetail;